import React, { useContext, useState } from 'react'
import ReactDOM from 'react-dom'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components/macro'
import { useUIDataSelector } from "../../service/hooks/useUIDataSelector";
import {
  AttachmentActions,
  AttachmentBoxFlexContainer,
  AttachmentCheckbox,
  DropdownButton,
  DropdownIcon,
  TextButton, UploadedDate
} from "../AttachmentBoxStyles";
import { UIAction } from "../../service/UIAction";
import { useDoAction } from "../../service/hooks/useDoAction";
import { ContextMenu, Icon, Li, ListSeparator, Loading } from "@startlibs/components";
import { dicomDateToDate, formatDateNoUTC } from "../../utils";
import { Accepted, Rejected } from "../../enums/RecordState";
import { UploaderConfigContext } from "../../service/UploaderConfigContext";
import { Uploaded } from "../../service/enums/RecordStatus";
import { usePopupToggle } from "@startlibs/core";
import { GroupAction } from "../../service/GroupAction";
import { useNewDownload, useViewRecord } from "../../service/utils/downloadFile";
import { NonCompliantDicom } from "../../enums/RecordFormat";
import { canPlay, canView } from "../../service/utils/recordUtils";
import { ADMIN, PATIENT, PROVIDER } from "../../enums/UserRoles";
import { useEffect } from 'react';
import { DEVICE, DISK } from '../../enums/UploaderStepsManagement';
import { jwtPostFetcher } from '../../utils/authFetch';
import { formatDate, getColor } from '@startlibs/utils';
import { transparentize } from 'polished';
import { FormattedMessage, useIntl } from "react-intl";
import { defaultDateFormat, simpleDateTimeFormat } from "../../utils/intlDateFormat";
import { convertBytesToMB, convertBytesAutoUnit } from '../../../../../src/javascripts/utils/utils';
import { AiPanelEV } from 'purview-design'
import { ICON_DOTDOTDOT } from '../../../../../purview-design/src/refs/Icons';

styled

// ponytail: module-level singleton — only one AI panel open at a time.
// Ceiling: breaks if multiple uploader instances coexist on the same page.
let _closeActiveAiPanel = null;

const FileSizeInfo = styled.p`
  color: rgba(0, 0, 0, 0.4);
  margin-top: 5px;
  margin-bottom: -0.5rem;
  font-size: 10px !important ;
  display: block;
  text-align: left;
  position: absolute;
  bottom: 10px;
`

// ── New v1.5 action button style ────────────────────────────────────────────
// Ghost/flat style — no border, subtle background on hover only

const AccessoryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 4px 8px;
  border-radius: 4px;
  background: transparent;
  border: none;
  color: ${props => getColor('main')(props)};
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  :hover {
    background: ${props => transparentize(0.92, getColor('main')(props))};
  }
  :active {
    background: ${props => transparentize(0.85, getColor('main')(props))};
  }
  ${props => props.disabled && css`
    opacity: 0.4;
    pointer-events: none;
  `}
  svg {
    flex-shrink: 0;
  }
`

// Details toggle: slight visual distinction with a subtle border
const GhostButton = styled(AccessoryButton)`
  border: 1px solid ${props => transparentize(0.7, getColor('main')(props))};
  background: ${props => transparentize(0.96, getColor('main')(props))};
  padding: 4px 8px 4px 10px;
  :hover {
    background: ${props => transparentize(0.9, getColor('main')(props))};
  }
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const DetailsToggle = styled(GhostButton)`
  margin-left: 0;
`

// Inline SVG icons (matching Figma ic stars chat, ic download, ic eye, ic dotdotdot)

const IcAskAI = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M7 3.5C7 5.433 5.433 7 3.5 7S0 5.433 0 3.5 1.567 0 3.5 0 7 1.567 7 3.5Z" fill="rgb(0,141,208)" fillOpacity="0.15"/>
    <path d="M14.5 1.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" fill="rgb(0,141,208)"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M9 8.5C9 11.538 6.538 14 3.5 14S-2 11.538-2 8.5-4.538 3 -1.5 3" fill="none"/>
    <path d="M2 8.5C4.485 8.5 6.5 6.485 6.5 4" stroke="rgb(0,141,208)" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="11.5" cy="9.5" r="4" stroke="rgb(0,141,208)" strokeWidth="1.5"/>
    <path d="M9.5 9.5h4M11.5 7.5v4" stroke="rgb(0,141,208)" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
)

const IcDotDotDot = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="4" cy="8" r="1.5" fill="rgb(0,141,208)"/>
    <circle cx="8" cy="8" r="1.5" fill="rgb(0,141,208)"/>
    <circle cx="12" cy="8" r="1.5" fill="rgb(0,141,208)"/>
  </svg>
)

const IcChevronDown = () => (
  <svg width="9" height="5" viewBox="0 0 9 5" fill="none">
    <path d="M1 1l3.5 3L8 1" stroke="rgb(0,141,208)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const IcChevronUp = () => (
  <svg width="9" height="5" viewBox="0 0 9 5" fill="none">
    <path d="M8 4L4.5 1 1 4" stroke="rgb(0,141,208)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const MoreActionsButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  position: relative;
  border-radius: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  color: ${props => getColor('main')(props)};
  :hover {
    background: ${props => transparentize(0.92, getColor('main')(props))};
  }
  :active {
    background: ${props => transparentize(0.85, getColor('main')(props))};
  }
`

export const RecordRowActions = ({ record, isMinified, group, isDragDisabled, deleteRecord, isExpanded, onToggleExpand, eveDocumentId, onStartSummarization, canOpenAiPanel, onOpenPage, aiReady, aiProcessingLabel }) => {
  const config = useContext(UploaderConfigContext)
  const intl = useIntl()
  const {
    role,
    mode,
    allowDownloadMedicalImages,
    isApp,
    withoutDelete,
    disabled,
    locations,
    isSelectingRecordsDirectMessage,
    setSelectedDirectMessageRecords, // DirectMessaging
    worklistViewerJwt,
  } = config

  const [aiPanelOpen, setAiPanelOpen] = useState(false)

  // Register this panel's close fn as the active singleton while open
  useEffect(() => {
    if (aiPanelOpen) {
      const closeFn = () => setAiPanelOpen(false)
      _closeActiveAiPanel = closeFn
      return () => { if (_closeActiveAiPanel === closeFn) _closeActiveAiPanel = null }
    }
  }, [aiPanelOpen])

  const handleAskAiClick = () => {
    if (aiPanelOpen) {
      setAiPanelOpen(false)
      return
    }
    // Close any other open AI panel first
    if (_closeActiveAiPanel) _closeActiveAiPanel()
    if (onStartSummarization) onStartSummarization()
    setAiPanelOpen(true)
  }

  const handleSendMessage = (question, onChunk, onDone, onError, onSources) => {
    if (!eveDocumentId) { onError(); return }
    const jwt = typeof worklistViewerJwt === 'function' ? worklistViewerJwt() : worklistViewerJwt
    const url = `/api/eve/documents/${eveDocumentId}/chat`
    
    let doneCalled = false
    let totalChunks = 0
    let totalBytes = 0
    const callDoneOnce = () => {
      if (doneCalled) return
      doneCalled = true
      // console.log(`[AiChat] stream finished — chunks: ${totalChunks}, bytes: ${totalBytes}`)
      if (totalBytes === 0) {
        console.warn('[AiChat] stream completed with no token data — treating as error')
        onError()
      } else {
        onDone()
      }
    }
    // console.log(`[AiChat] POST ${url} — question length: ${question.length}`)
    fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({ question }),
    })
      .then(res => {
        // console.log(`[AiChat] response status: ${res.status}, content-type: ${res.headers.get('content-type')}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        if (!res.body) {
          // console.warn('[AiChat] no ReadableStream body — falling back to .text()')
          return res.text().then(text => {
            // console.log(`[AiChat] fallback text length: ${text.length}`)
            if (text) { onChunk(text); totalBytes += text.length; totalChunks++ }
            callDoneOnce()
          })
        }
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let currentEvent = ''
        let buffer = '' // ponytail: buffer handles lines split across network chunks
        const processLine = (line) => {
          if (!line.trim()) { currentEvent = ''; return }
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim()
            return
          }
          if (line.startsWith('data:')) {
            const payload = line.slice(5)
            if (currentEvent === 'done') { callDoneOnce(); return }
            if (currentEvent === 'sources' && payload && onSources) {
              try {
                const parsed = JSON.parse(payload)
                onSources(parsed)
                // Fetch image blob URLs for IMAGE kind sources
                const imageSources = parsed.filter(s => s.kind === 'IMAGE' && s.pictureRef && s.documentId)
                if (imageSources.length > 0) {
                  const token = typeof worklistViewerJwt === 'function' ? worklistViewerJwt() : worklistViewerJwt
                  Promise.all(imageSources.map(s =>
                    fetch(`/api/eve/documents/${s.documentId}/pictures/${s.pictureRef}`, {
                      headers: { 'Authorization': `Bearer ${token}` },
                    })
                      .then(r => r.ok ? r.blob() : null)
                      .then(blob => blob ? { n: s.n, imageUrl: URL.createObjectURL(blob) } : null)
                      .catch(() => null)
                  )).then(results => {
                    const urlMap = {}
                    results.forEach(r => { if (r) urlMap[r.n] = r.imageUrl })
                    if (Object.keys(urlMap).length > 0) {
                      onSources(parsed.map(s => urlMap[s.n] ? { ...s, imageUrl: urlMap[s.n] } : s))
                    }
                  })
                }
              } catch(e) { console.warn('[AiChat] sources parse error', e, 'payload length:', payload.length) }
            }
            if (currentEvent === 'token' && payload) {
              totalChunks++
              totalBytes += payload.length
              onChunk(payload)
            }
          }
        }
        const pump = () => reader.read().then(({ done, value }) => {
          if (doneCalled) return
          if (done) {
            // Process any remaining buffered content
            if (buffer.trim()) processLine(buffer)
            buffer = ''
            // console.log('[AiChat] reader done signal')
            callDoneOnce()
            return
          }
          buffer += decoder.decode(value, { stream: true })
          // Split on newlines; last element is incomplete (keep in buffer)
          const lines = buffer.split('\n')
          buffer = lines.pop() // keep the incomplete trailing segment
          lines.forEach(processLine)
          return pump()
        })
        return pump()
      })
      .catch((err) => { console.warn('[AiChat] stream error:', err); if (!doneCalled) { doneCalled = true; onError() } })
  }

  const isSelectMode = useUIDataSelector('selectMode')
  const selectedRecords = useUIDataSelector('selectedRecords')
  const doAction = useDoAction()

  const isViewable = canView(record) && record.key !== NonCompliantDicom
  const isPlayable = canPlay(record)
  const hideAiFeedbacks = typeof window !== 'undefined' && ['true', '1'].includes(String(window.HIDE_AI_FEEDBACKS).toLowerCase())

  useEffect(() => {

    if (record?.recordUID && role == PATIENT && (record?.fileExtension?.toLowerCase() == 'jpg' || record?.fileExtension?.toLowerCase() == 'jpeg')) {

      // Files uploaded by patient will be reorganized later. 
      // We can make sure that the files will be hidden if there are more than 10 uploaded in a device or disk.
      // We need this to make sure that the combination of 6 in a device and 8 in a disk wont be hidden.
      if (mode == DEVICE) {
        var patDevJpgs = JSON.parse(window.localStorage.getItem("patDevJpgs")) || []
        if (!patDevJpgs.includes(record.recordUID)) {
          patDevJpgs = patDevJpgs.concat(record.recordUID)
          window.localStorage.setItem("patDevJpgs", JSON.stringify(patDevJpgs))
        }
      }
      if (mode == DISK) {
        var patDiscJpgs = JSON.parse(window.localStorage.getItem("patDiscJpgs")) || []
        if (!patDiscJpgs.includes(record.recordUID)) {
          patDiscJpgs = patDiscJpgs.concat(record.recordUID)
          window.localStorage.setItem("patDiscJpgs", JSON.stringify(patDiscJpgs))
        }
      }
    }
  }, [record.recordUID])
  const submitedBy = (record) => {
    // return ''
    if (role == ADMIN && record.stateContext && record.stateContext.Submitted && record.stateContext.Submitted.by) {
      return (record.stateContext.Submitted.by.includes("@") ? (" by " + record.stateContext.Submitted.by) : record.stateContext.Submitted.by.includes("Patient") ? ' by patient' : locations.find(({ id }) => id == record.stateContext.Submitted.by)?.name !== undefined ? " by " + locations.find(({ id }) => id == record.stateContext.Submitted.by)?.name : '')
    } else {
      return ''
    }
  }

  if (isSelectMode) {
    if (isSelectingRecordsDirectMessage) {
      const toggleSelection = () => {
        setSelectedDirectMessageRecords(prev =>
          prev.includes(record.key) ? prev.filter(f => f !== record.key) : [...prev, record.key]
        )
        doAction(UIAction.ToggleSelectedRecord, record.key)
      }
      return <ActionsRow>
        {(allowDownloadMedicalImages || (!allowDownloadMedicalImages && !isViewable && !isPlayable)) && !isApp && (
          <DownloadAccessoryButton config={config} record={record} />
        )}
        {isViewable && !isApp && (
          <ViewAccessoryButton config={config} record={record} hideDownloadButton={!allowDownloadMedicalImages} />
        )}
        {isPlayable && !isApp && (
          <ViewAccessoryButton config={config} record={record} hideDownloadButton={!allowDownloadMedicalImages} icon="video" label={intl.formatMessage({
            defaultMessage: "Play",
            description: "Uploader, play button"
          })} />
        )}
        <div onClick={toggleSelection}>
          <AttachmentCheckbox
            compact
            raw
            value={selectedRecords.includes(record.key)}
            setValue={toggleSelection}
          />
        </div>
        {!!record.fileSize && <AttachmentBoxFlexContainer>
          <FileSizeInfo>
          File size: {convertBytesAutoUnit(record.fileSize)}
          </FileSizeInfo>
        </AttachmentBoxFlexContainer>}
      </ActionsRow>
    }
    return <>
      <div onClick={() => doAction(UIAction.ToggleSelectedRecord, record.key)}>
        <AttachmentCheckbox
          raw
          value={selectedRecords.includes(record.key)}
          setValue={() => doAction(UIAction.ToggleSelectedRecord, record.key)}
        />
      </div>
    </>
  }

  if (record.status !== Uploaded && record.status !== Accepted) {
    return null
  }

  if (isMinified) {
    return <AttachmentActions>
      <div className="buttons-wrapper">
        {
          !disabled && !withoutDelete &&
          <a className="link" onClick={() => doAction(UIAction.ToggleDeleteRecordDialog, record)}><FormattedMessage
            defaultMessage="Delete"
            description="Delete button"
          /></a>
        }
      </div>
    </AttachmentActions>
  }

  // v1.5 layout: inline action buttons + Details toggle
  return <>
    <ActionsRow>
      {/* Ask AI */}
      {role === ADMIN && canOpenAiPanel && <AccessoryButton onClick={handleAskAiClick}>
        <Icon icon="stars-chat"/>
        <FormattedMessage defaultMessage="Ask AI" description="Ask AI button" />
      </AccessoryButton>}

      {/* Download */}
      {(allowDownloadMedicalImages || (!allowDownloadMedicalImages && !isViewable && !isPlayable)) && !isApp && (
        <DownloadAccessoryButton config={config} record={record} />
      )}

      {/* View */}
      {isViewable && !isApp && (
        <ViewAccessoryButton config={config} record={record} hideDownloadButton={!allowDownloadMedicalImages} />
      )}

      {/* Play */}
      {isPlayable && !isApp && (
        <ViewAccessoryButton config={config} record={record} hideDownloadButton={!allowDownloadMedicalImages} icon="video" label={intl.formatMessage({
          defaultMessage: "Play",
          description: "Uploader, play button"
        })} />
      )}

      {/* More ··· */}
      {!disabled && !withoutDelete && (
        <MoreActionsWrap role={role} record={record} group={group} isDragDisabled={isDragDisabled} doAction={doAction} deleteRecord={deleteRecord} />
      )}

      {/* Details toggle */}
      {onToggleExpand && (
        <DetailsToggle onClick={onToggleExpand}>
          <FormattedMessage defaultMessage="Details" description="Details toggle button" />
          {isExpanded ? <IcChevronUp /> : <IcChevronDown />}
        </DetailsToggle>
      )}
    </ActionsRow>

    {/* AI Panel — fixed bottom-right overlay via portal */}
    {aiPanelOpen && ReactDOM.createPortal(
      <div key={record.key} style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        borderRadius: '6px',
        overflow: 'hidden',
      }}>
        <AiPanelEV
          initialState={aiReady ? 'initial' : 'loading'}
          recordClass={record.recordClass}
          fileExtension={record.fileExtension}
          recordFileName={getFilenameFromRecord(intl, record) || ''}
          onClose={() => setAiPanelOpen(false)}
          onSendMessage={aiReady ? handleSendMessage : undefined}
          onOpenSource={onOpenPage}
          processingLabel={aiProcessingLabel}
          hideFeedbacks={hideAiFeedbacks}
        />
      </div>,
      document.body
    )}
  </>
}

export const getFilenameFromRecord = (intl, record) => {

  const { description, fileExtension, info, key, recordUIDs } = record;

  // Handle NonCompliantDicom case
  if (key === "NonCompliantDicom") {
    const count = recordUIDs?.length || 0;
    if (count > 0) {
      return `${count} Non compliant DICOM file${count > 1 ? 's' : ''}`;
    } else {
      return `All Non compliant DICOM files`;
    }
  }

  let name = description || (info && info.description) || (info && info.studyDescription) || "";
  // let ext = fileExtension || (info && info.fileExtension) || "";

  // Handle DicomStudy case
  if ((info && info.type === "DicomStudy") && (!name || !info.studyDescription)) {
    const studyDate = dicomDateToDate(info.studyDate)
    const formattedDate = !isNaN(studyDate) && `(${formatDate(studyDate, defaultDateFormat(intl, "-"))})`

    if (studyDate) {
      name = `DICOM Study ${formattedDate ? formattedDate : ''}`;
    } else {
      name = `DICOM Study`;
    }
  }

  return name;
}


// v1.5 Download accessory button
const DownloadAccessoryButton = ({ config, record }) => {
  const intl = useIntl()
  const {
    requestId,
    apiEndpoints: { downloadFiles: downloadUrl },
    worklistViewerJwt,
    role,
    isSelectingRecordsDirectMessage
  } = config

  const startDownloadAndLog = () => {
    tryDownload()
    if (role !== PROVIDER) {
      try {
        const fileName = getFilenameFromRecord(intl, record)
        jwtPostFetcher(worklistViewerJwt)(`/api/audit/study/downloadRequested?requestId=${requestId}`, {
          recordDescriptions: [fileName.trim()],
        })
      } catch (e) {
        console.log(e)
      }
    }
  }

  const [tryDownload, downloadIsLoading, downloadIsStating] = useNewDownload(worklistViewerJwt, record.recordUIDs || [record.recordUID], downloadUrl, record.fileName + (record.fileExtension ? "." : "") + record.fileExtension, requestId)

  return (
    <AccessoryButton
      disabled={record.quarantined}
      onClick={(e) => {
        if (isSelectingRecordsDirectMessage) { e.stopPropagation(); e.preventDefault() }
        startDownloadAndLog()
      }}
    >
      {downloadIsLoading
        ? <><Loading size={12} borderWidth={2} /><FormattedMessage defaultMessage="Preparing..." description="Uploader, download button, preparing download" /></>
        : downloadIsStating
          ? <><Icon icon="check" css="font-size:14px;margin-right:2px;" /><FormattedMessage defaultMessage="Started" description="Uploader, download button, started download" /></>
          : <><Icon icon="download" css="font-size:14px;margin-right:2px;" /><FormattedMessage defaultMessage="Download" description="Uploader, download button" /></>
      }
    </AccessoryButton>
  )
}

// v1.5 View / Play accessory button
const ViewAccessoryButton = ({ config, record, icon, label, hideDownloadButton = false }) => {
  const intl = useIntl()
  const {
    requestId,
    worklistViewerJwt: expertViewJwt,
    role,
    isSelectingRecordsDirectMessage
  } = config

  const openViewerAndLog = () => {
    openViewerLink()
    if (role !== PROVIDER) {
      try {
        const fileName = getFilenameFromRecord(intl, record)
        jwtPostFetcher(expertViewJwt)(`/api/audit/study/viewRequested?requestId=${requestId}`, {
          recordDescriptions: [fileName.trim()],
        })
      } catch (e) {
        console.log(e)
      }
    }
  }

  const [openViewerLink, viewerIsLoading] = useViewRecord(config, record, hideDownloadButton)

  return (
    <AccessoryButton
      disabled={record.quarantined}
      onClick={(e) => {
        if (isSelectingRecordsDirectMessage) { e.stopPropagation(); e.preventDefault() }
        openViewerAndLog()
      }}
    >
      {icon === 'video'
        ? <Icon icon="video" css="font-size:14px;margin-right:2px;" />
        : <Icon icon="view" css="font-size:14px;margin-right:2px;" />
      }
      {label || <FormattedMessage defaultMessage="View" description="View button" />}
    </AccessoryButton>
  )
}

// v1.5 More ··· dropdown (context menu with move/delete)
const MoreActionsWrap = ({ role, group, isDragDisabled, doAction, record, deleteRecord }) => {
  const intl = useIntl()
  const groups = useUIDataSelector('groups')
  const contextMenu = usePopupToggle()

  const addNewGroup = () => doAction(GroupAction.AddGroup)
  const moveTo = (groupId) => doAction(GroupAction.SimpleMoveRecord, record, group.id, groupId)

  const moveToUngroup = () => {
    let ungroup = groups.filter(g => g.id === 'ungrouped')
    if (!ungroup.length) {
      doAction(GroupAction.UpdateGroups, (groups) =>
        [{ name: '', id: 'ungrouped', items: [] }].concat(groups.filter(g => g.id !== 'ungrouped'))
      )
    }
    moveTo("ungrouped")
  }

  return (
    <MoreActionsButton onClick={contextMenu.open}>
      <Icon icon={ICON_DOTDOTDOT} />
      {contextMenu.isOpen && (
        <ContextMenu my="top right" at="bottom right">
          {(record.fileExtension?.toLowerCase() === "pdf" && !record.quarantined && role === ADMIN) && (
            <Li
              icon='split-doc'
              label={intl.formatMessage({ defaultMessage: 'Split document', description: "Uploader, split document menu item" })}
              onClick={() => doAction(UIAction.ToggleSplitPDFDialog, record)}
            />
          )}
          {!isDragDisabled && (
            <Li
              label={intl.formatMessage({ defaultMessage: "Move to...", description: "Uploader, 'move to' menu item" })}
              icon="move-group"
            >
              <ContextMenu my="top left" at="top right">
                {groups.map(groupItem =>
                  groupItem.name
                    ? <Li
                        key={groupItem.id}
                        disabled={((group.id === groupItem.id) || ((record.key === NonCompliantDicom || record?.quarantined == true) && groupItem.id === 'notclass'))}
                        icon={group.id === groupItem.id && 'check'}
                        label={groupItem.name}
                        onClick={() => moveTo(groupItem.id)}
                      />
                    : null
                )}
                <ListSeparator />
                <Li
                  label={intl.formatMessage({ defaultMessage: "Move to a new group", description: "Uploader, move to new group menu item" })}
                  icon="plus-circle"
                  onClick={() => moveTo(addNewGroup())}
                />
                {group.id !== 'ungrouped' && <ListSeparator />}
                {group.id !== 'ungrouped' && (
                  <Li
                    disabled={group.id === 'ungrouped'}
                    icon="remove-from-group"
                    label={intl.formatMessage({ defaultMessage: "Remove from this group", description: "Uploader, remove from group menu item" })}
                    onClick={moveToUngroup}
                  />
                )}
              </ContextMenu>
            </Li>
          )}
          <Li
            label={record.key === NonCompliantDicom
              ? intl.formatMessage({ defaultMessage: "Delete non compliant files", description: "Uploader, delete non compliant files menu item" })
              : intl.formatMessage({ defaultMessage: "Delete record", description: "Uploader, delete record menu item" })}
            icon="delete"
            onClick={deleteRecord || (() => doAction(UIAction.ToggleDeleteRecordDialog, record))}
          />
        </ContextMenu>
      )}
    </MoreActionsButton>
  )
}

// Keep old DownloadButton/ViewButton for selectMode usage
const DownloadButton = DownloadAccessoryButton
const ViewButton = ViewAccessoryButton